/**
 * Turning a move into something a person can follow.
 *
 * A single move can hide a lot: ending a turn runs that player's Zombie
 * Rising and, at the end of a round, the horde's own turn; online, one reply
 * from the server can carry several bot turns. This module splits such a
 * batch into beats: a board to show, the events behind it (for effects,
 * sounds and log lines), a caption, an optional banner and floating labels,
 * and how long to hold it. The store plays beats one after another.
 *
 * Pure functions only: no timers, no store.
 */
import type { Action, GameEvent, GameState, PlayerId, Territory, TerritoryId } from '@horde/engine';
import { CARD_BY_ID } from '@horde/engine';
import { playerName, territoryName } from './format';

export interface Floater {
  territoryId: TerritoryId;
  text: string;
  tone: 'horde' | 'bad' | 'good';
}

export interface Banner {
  title: string;
  line: string;
  tone: 'rising' | 'horde' | 'fallen';
}

export interface Beat {
  /** The board to show during this beat. */
  game: GameState;
  /** What happened in this beat: drives map effects, sounds and log lines. */
  events: GameEvent[];
  /** How long to hold before the next beat. */
  ms: number;
  caption?: string | null;
  banner?: Banner | null;
  floaters?: Floater[];
  /** The move behind this beat, if any (for the attack sound cue). */
  action?: Pick<Action, 'type' | 'playerId'> | null;
  /** A one-line recap of the rising in this move, kept after playback. */
  risingSummary?: string | undefined;
  /** For a player knocked out in this beat: how they fell (the cause may be in an earlier beat). */
  fallCause?: string;
}

/** The public board after one move, as the server sends it in a step. */
export interface BoardSnapshot {
  round: number;
  phase: GameState['phase'];
  currentPlayerIndex: number;
  territories: Record<TerritoryId, [string | null, number, number]>;
}

export interface Step {
  playerId: PlayerId;
  actionType: Action['type'];
  events: GameEvent[];
  board: BoardSnapshot;
}

export interface Pace {
  /** Hold after a move that is not the viewer's own. */
  move: (actionType: Action['type']) => number;
  /** Skip the in-between beats: just show where things ended up. */
  fast: boolean;
  /** Whose moves need no narration (they made them). */
  isViewer: (playerId: PlayerId) => boolean;
}

const MOVE_MS: Partial<Record<Action['type'], number>> = {
  attack: 1050,
  place: 700,
  drawCard: 850,
  playCard: 900,
  occupy: 550,
  fortify: 850,
  endAttack: 350,
  endTurn: 450,
};

export function moveHold(type: Action['type']): number {
  return MOVE_MS[type] ?? 600;
}

const RISING_BANNER_MS = 900;
const RISING_TERRITORY_MS = 520;
const HORDE_GROWTH_MS = 1000;
const HORDE_PUSH_MS = 700;
const ELIMINATED_MS = 1400;
/** Long risings speed up so a turn change never drags past this. */
const RISING_BUDGET_MS = 7000;

// ---------------------------------------------------------------------------
// Board deltas: rebuild the in-between boards a rising passes through
// ---------------------------------------------------------------------------

function cloneTerritories(t: GameState['territories']): GameState['territories'] {
  const out: GameState['territories'] = {};
  for (const [id, v] of Object.entries(t)) out[id] = { ...v };
  return out;
}

function settleOwner(t: Territory): void {
  if (t.owner === 'horde' && t.zombies <= 0) t.owner = null;
  if (t.owner === null && t.zombies > 0) t.owner = 'horde';
}

/** Apply what one event did to the board. Only the board; the true end state replaces it anyway. */
export function applyEvent(state: GameState, e: GameEvent): GameState {
  const territories = cloneTerritories(state.territories);
  const t = (id: TerritoryId) => territories[id];
  switch (e.type) {
    case 'reinforced': {
      const x = t(e.territoryId);
      if (x) x.armies += e.count;
      break;
    }
    case 'fortified': {
      const a = t(e.from);
      const b = t(e.to);
      if (a) a.armies -= e.count;
      if (b) b.armies += e.count;
      break;
    }
    case 'zombiesSpawned': {
      const x = t(e.territoryId);
      if (x) {
        x.zombies += e.count;
        settleOwner(x);
      }
      break;
    }
    case 'bitten':
    case 'armiesLost': {
      const x = t(e.territoryId);
      if (x) x.armies = Math.max(0, x.armies - e.count);
      break;
    }
    case 'overrun': {
      const x = t(e.territoryId);
      if (x) {
        x.owner = 'horde';
        x.armies = 0;
      }
      break;
    }
    case 'zombiesMoved': {
      const a = t(e.from);
      const b = t(e.to);
      if (a) {
        a.zombies = Math.max(0, a.zombies - e.count);
        settleOwner(a);
      }
      if (b) {
        b.zombies += e.count;
        settleOwner(b);
      }
      break;
    }
    case 'zombiesRemoved': {
      const x = t(e.territoryId);
      if (x) {
        x.zombies = Math.max(0, x.zombies - e.count);
        settleOwner(x);
      }
      break;
    }
    default:
      return state;
  }
  return { ...state, territories };
}

const applyAll = (state: GameState, events: GameEvent[]) => events.reduce(applyEvent, state);

/** The final state with an earlier step's public board: for playing a batch back. */
export function withBoard(base: GameState, board: BoardSnapshot): GameState {
  const territories: GameState['territories'] = {};
  for (const [id, [owner, armies, zombies]] of Object.entries(board.territories)) {
    territories[id] = {
      ...(base.territories[id] ?? { id }),
      id,
      owner: owner as Territory['owner'],
      armies,
      zombies,
    };
  }
  const owns = new Set(Object.values(territories).map((t) => t.owner));
  return {
    ...base,
    round: board.round,
    phase: board.phase,
    currentPlayerIndex: board.currentPlayerIndex,
    territories,
    // Someone knocked out later in the batch is still in at this point.
    players: base.players.map((p) => ({ ...p, eliminated: p.eliminated && !owns.has(p.id) })),
    outcome: board.phase === 'ended' ? base.outcome : null,
    pendingOccupy: null,
  };
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function possessive(name: string): string {
  return name === 'You' ? 'Your' : `${name}'s`;
}

/** One line on what a move did, for when someone else is playing. */
export function captionFor(
  events: GameEvent[],
  state: GameState,
  playerId: PlayerId,
  actionType: Action['type'],
): string | null {
  const who = playerName(state, playerId);
  const roll = events.find((e) => e.type === 'diceRolled');
  if (roll && roll.type === 'diceRolled') {
    if (roll.from === roll.to) {
      return `${who} purges ${territoryName(roll.from)} · kills ${plural(roll.defenderLosses, 'zombie')}, loses ${roll.attackerLosses}`;
    }
    const took = events.some((e) => e.type === 'territoryCaptured' && e.territoryId === roll.to);
    return `${who} attacks ${territoryName(roll.to)} from ${territoryName(roll.from)} · loses ${roll.attackerLosses}, kills ${roll.defenderLosses}${took ? ` · takes ${territoryName(roll.to)}` : ''}`;
  }
  const captured = events.find((e) => e.type === 'territoryCaptured');
  if (captured && captured.type === 'territoryCaptured')
    return `${who} takes ${territoryName(captured.territoryId)}`;
  const placed = events.filter(
    (e): e is Extract<GameEvent, { type: 'reinforced' }> => e.type === 'reinforced',
  );
  if (placed.length > 0) {
    const total = placed.reduce((n, e) => n + e.count, 0);
    const where = new Set(placed.map((e) => e.territoryId));
    return where.size === 1
      ? `${who} reinforces ${territoryName(placed[0]!.territoryId)} (+${total})`
      : `${who} places ${plural(total, 'unit')}`;
  }
  const drawn = events.find((e) => e.type === 'cardDrawn');
  if (drawn && drawn.type === 'cardDrawn') {
    const card = CARD_BY_ID[drawn.cardId];
    return card ? `${who} draws ${card.name}` : `${who} draws a card and keeps it`;
  }
  const played = events.find((e) => e.type === 'cardResolved');
  if (played && played.type === 'cardResolved' && played.target) {
    return `${who} plays ${CARD_BY_ID[played.cardId]?.name ?? 'a card'} on ${territoryName(played.target)}`;
  }
  const moved = events.find((e) => e.type === 'fortified');
  if (moved && moved.type === 'fortified') {
    return `${who} moves ${moved.count} from ${territoryName(moved.from)} to ${territoryName(moved.to)}`;
  }
  if (actionType === 'occupy') return `${who} moves in`;
  if (actionType === 'endAttack') return `${who} stops attacking`;
  if (actionType === 'endTurn') return `${who} ends their turn`;
  return null;
}

/** "Rising: 3 zombies rose, 1 unit bitten, Mexico overrun." */
export function risingSummary(events: GameEvent[]): string | null {
  let spawned = 0;
  let bitten = 0;
  const overrun: string[] = [];
  let pushes = 0;
  for (const e of events) {
    if (e.type === 'zombiesSpawned') spawned += e.count;
    else if (e.type === 'bitten') bitten += e.count;
    else if (e.type === 'overrun') overrun.push(territoryName(e.territoryId));
    else if (e.type === 'hordeSpread') pushes++;
  }
  const parts: string[] = [];
  if (spawned) parts.push(`${plural(spawned, 'zombie')} rose`);
  if (bitten) parts.push(`${plural(bitten, 'unit')} bitten`);
  if (overrun.length) parts.push(`${overrun.join(', ')} overrun`);
  if (pushes) parts.push(`the horde pushed into ${plural(pushes, 'territory', 'territories')}`);
  return parts.length ? parts.join(', ') : null;
}

// ---------------------------------------------------------------------------
// Beats
// ---------------------------------------------------------------------------

const isBoundary = (e: GameEvent) =>
  e.type === 'risingStarted' ||
  e.type === 'phaseChanged' ||
  e.type === 'playerEliminated' ||
  e.type === 'gameEnded';

/** Group a player's rising by territory, in the order things happened. */
function byTerritory(events: GameEvent[]): GameEvent[][] {
  const groups = new Map<TerritoryId, GameEvent[]>();
  for (const e of events) {
    const id = 'territoryId' in e ? (e.territoryId as TerritoryId) : null;
    if (!id) continue;
    if (!groups.has(id)) groups.set(id, []);
    groups.get(id)!.push(e);
  }
  return [...groups.values()];
}

function labelFor(group: GameEvent[]): Floater | null {
  const id = (group[0] as { territoryId: TerritoryId }).territoryId;
  if (group.some((e) => e.type === 'overrun'))
    return { territoryId: id, text: 'Overrun!', tone: 'horde' };
  const parts: string[] = [];
  let tone: Floater['tone'] = 'horde';
  for (const e of group) {
    if (e.type === 'zombiesSpawned') parts.push(`+${plural(e.count, 'zombie')}`);
    if (e.type === 'bitten') {
      parts.push(`${e.count} bitten`);
      tone = 'bad';
    }
  }
  return parts.length ? { territoryId: id, text: parts.join(' · '), tone } : null;
}

function eliminationBanner(
  e: Extract<GameEvent, { type: 'playerEliminated' }>,
  state: GameState,
  context: GameEvent[],
): Banner {
  return {
    title: `${playerName(state, e.playerId)} ${playerName(state, e.playerId) === 'You' ? 'have' : 'has'} fallen`,
    line: fallCause(e.playerId, state, context),
    tone: 'fallen',
  };
}

/** How a player lost their last territory, from the events around it. */
export function fallCause(playerId: PlayerId, state: GameState, context: GameEvent[]): string {
  for (let i = context.length - 1; i >= 0; i--) {
    const e = context[i]!;
    if (e.type === 'overrun' && e.previousOwner === playerId)
      return `${territoryName(e.territoryId)} was overrun by the horde`;
    if (e.type === 'territoryCaptured' && e.from === playerId) {
      return `${territoryName(e.territoryId)} was taken by ${e.to === 'horde' ? 'the horde' : playerName(state, e.to as PlayerId)}`;
    }
  }
  return 'Their last territory fell';
}

/**
 * Beats for one move: `prev` is the board before it, `next` the true board
 * after, `events` what the engine reported. A move with no rising is one
 * beat. A move that ends a turn becomes: the move itself, the player's
 * Zombie Rising (a banner, then each affected territory in turn), the horde's
 * turn at a round's end (growth everywhere at once, then each push), any
 * eliminations, and the new turn.
 */
export function beatsForMove(
  prev: GameState,
  next: GameState,
  events: GameEvent[],
  move: { playerId: PlayerId; type: Action['type'] },
  pace: Pace,
): Beat[] {
  const viewer = pace.isViewer(move.playerId);
  const hold = viewer ? 0 : pace.move(move.type);
  const caption = viewer ? null : captionFor(events, next, move.playerId, move.type);
  const action = { type: move.type, playerId: move.playerId };
  const firstRising = events.findIndex((e) => e.type === 'risingStarted');
  const summary = risingSummary(events) ?? undefined;
  const eliminated = events.filter(
    (e): e is Extract<GameEvent, { type: 'playerEliminated' }> => e.type === 'playerEliminated',
  );

  if (pace.fast || firstRising < 0) {
    // Nothing to stage: one beat, though an elimination still gets its banner.
    const banner = eliminated[0] ? eliminationBanner(eliminated[0], next, events) : null;
    return [
      {
        game: next,
        events,
        ms: hold,
        caption,
        banner,
        action,
        risingSummary: firstRising >= 0 ? summary : undefined,
      },
    ];
  }

  const beats: Beat[] = [];
  let board = prev;
  const staged = (b: GameState) => ({ ...prev, territories: b.territories });

  const pre = events.slice(0, firstRising);
  if (pre.length > 0) {
    board = applyAll(board, pre);
    beats.push({ game: staged(board), events: pre, ms: hold || 350, caption, action });
  }

  const tail: GameEvent[] = [];
  let i = firstRising;
  const risingBeats: Beat[] = [];
  while (i < events.length) {
    const e = events[i]!;
    if (e.type === 'risingStarted') {
      let j = i + 1;
      while (j < events.length && !isBoundary(events[j]!)) j++;
      const section = events.slice(i + 1, j);
      if (e.playerId !== null) {
        const name = playerName(next, e.playerId);
        const groups = byTerritory(section);
        risingBeats.push({
          game: staged(board),
          events: [e],
          ms: RISING_BANNER_MS,
          banner: {
            title: 'Zombie Rising',
            line: groups.length
              ? `${possessive(name)} territories`
              : `${possessive(name)} territories · all quiet`,
            tone: 'rising',
          },
          caption: `Zombie Rising · ${possessive(name)} territories`,
        });
        for (const g of groups) {
          board = applyAll(board, g);
          const label = labelFor(g);
          risingBeats.push({
            game: staged(board),
            events: g,
            ms: RISING_TERRITORY_MS,
            floaters: label ? [label] : [],
          });
        }
      } else {
        const growth = section.filter((x) => x.type === 'zombiesSpawned');
        const rest = section.filter((x) => x.type !== 'zombiesSpawned');
        risingBeats.push({
          game: staged(board),
          events: [e],
          ms: RISING_BANNER_MS,
          banner: { title: 'The horde rises', line: `End of round ${e.round}`, tone: 'horde' },
          caption: `The horde rises · end of round ${e.round}`,
        });
        if (growth.length > 0) {
          board = applyAll(board, growth);
          risingBeats.push({
            game: staged(board),
            events: growth,
            ms: HORDE_GROWTH_MS,
            floaters: growth.map((g) => ({
              territoryId: (g as { territoryId: TerritoryId }).territoryId,
              text: `+${(g as { count: number }).count}`,
              tone: 'horde' as const,
            })),
          });
        }
        // Each push is a hordeSpread followed by the zombiesMoved it caused.
        for (let k = 0; k < rest.length; k++) {
          const push = rest[k]!;
          if (push.type !== 'hordeSpread') continue;
          const moved = rest[k + 1]?.type === 'zombiesMoved' ? rest[++k]! : null;
          const group = moved ? [push, moved] : [push];
          board = applyAll(board, group);
          const count = moved && moved.type === 'zombiesMoved' ? moved.count : 1;
          risingBeats.push({
            game: staged(board),
            events: group,
            ms: HORDE_PUSH_MS,
            caption: `The horde pushes ${plural(count, 'zombie')} into ${territoryName(push.to)}`,
            floaters: [
              { territoryId: push.to, text: `+${plural(count, 'zombie')}`, tone: 'horde' },
            ],
          });
        }
      }
      i = j;
    } else if (e.type === 'playerEliminated') {
      risingBeats.push({
        game: staged(board),
        events: [e],
        ms: ELIMINATED_MS,
        banner: eliminationBanner(e, next, events),
        fallCause: fallCause(e.playerId, next, events),
      });
      i++;
    } else {
      tail.push(e);
      i++;
    }
  }

  // Keep a long rising brisk.
  const total = risingBeats.reduce((n, b) => n + b.ms, 0);
  const scale = total > RISING_BUDGET_MS ? RISING_BUDGET_MS / total : 1;
  for (const b of risingBeats) beats.push({ ...b, ms: Math.round(b.ms * scale) });

  const nextPlayer = next.outcome ? null : next.players[next.currentPlayerIndex];
  beats.push({
    game: next,
    events: tail,
    ms: 300,
    caption:
      nextPlayer && !pace.isViewer(nextPlayer.id)
        ? `${playerName(next, nextPlayer.id)}'s turn`
        : null,
    risingSummary: summary,
  });
  return beats;
}

/** Beats for a server batch: each step against the board before it, ending on `final`. */
export function beatsForSteps(
  prev: GameState,
  final: GameState,
  steps: Step[],
  pace: Pace,
): Beat[] {
  const beats: Beat[] = [];
  let before = prev;
  steps.forEach((s, i) => {
    const after = i === steps.length - 1 ? final : withBoard(final, s.board);
    beats.push(
      ...beatsForMove(before, after, s.events, { playerId: s.playerId, type: s.actionType }, pace),
    );
    before = after;
  });
  return beats;
}
